const latin1Decoder = new TextDecoder('latin1');
let win1252Decoder;
try { win1252Decoder = new TextDecoder('windows-1252'); } catch { win1252Decoder = latin1Decoder; }

function bytesToLatin1(bytes) {
  let out = '';
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) {
    out += String.fromCharCode(...bytes.subarray(i, i + step));
  }
  return out;
}

function latin1ToBytes(text) {
  const out = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) out[i] = text.charCodeAt(i) & 0xff;
  return out;
}

function findObjects(bytes) {
  const text = bytesToLatin1(bytes);
  const re = /(?:^|[\r\n])(\d+)\s+(\d+)\s+obj\b/g;
  const matches = [];
  let m;
  while ((m = re.exec(text))) {
    const prefix = m[0].length - m[0].replace(/^[\r\n]+/, '').length;
    const headerStart = m.index + prefix;
    matches.push({ number: Number(m[1]), generation: Number(m[2]), headerStart, bodyStart: re.lastIndex });
  }
  const map = new Map();
  for (let i = 0; i < matches.length; i++) {
    const cur = matches[i];
    const nextStart = i + 1 < matches.length ? matches[i + 1].headerStart : text.length;
    let end = text.lastIndexOf('endobj', nextStart);
    if (end < cur.bodyStart) end = nextStart;
    map.set(cur.number, {
      number: cur.number,
      generation: cur.generation,
      bodyText: text.slice(cur.bodyStart, end),
      bodyBytes: bytes.subarray(cur.bodyStart, end),
    });
  }
  return map;
}

function parseIndirectRef(text, key) {
  const re = new RegExp(`/${key}\\s+(\\d+)\\s+(\\d+)\\s+R`);
  const m = text.match(re);
  return m ? Number(m[1]) : null;
}

function parseContentsRefs(text) {
  const arrayMatch = text.match(/\/Contents\s*\[([^\]]+)\]/s);
  if (arrayMatch) return [...arrayMatch[1].matchAll(/(\d+)\s+\d+\s+R/g)].map(m => Number(m[1]));
  const one = parseIndirectRef(text, 'Contents');
  return one ? [one] : [];
}


function parseAnnotsRefs(text, objects) {
  const arrayMatch = text.match(/\/Annots\s*\[([^\]]+)\]/s);
  if (arrayMatch) return [...arrayMatch[1].matchAll(/(\d+)\s+\d+\s+R/g)].map(m => Number(m[1]));
  const one = parseIndirectRef(text, 'Annots');
  if (!one) return [];
  const body = objects.get(one)?.bodyText || '';
  return [...body.matchAll(/(\d+)\s+\d+\s+R/g)].map(m => Number(m[1]));
}

function readPdfStringAfterKey(text, key) {
  const re = new RegExp(`/${key}\\b`, 'g');
  let m;
  while ((m = re.exec(text))) {
    let i = m.index + m[0].length;
    while (/\s/.test(text[i] || '')) i++;
    if (text[i] === '(') {
      const token = readLiteralToken(text, i);
      return decodeLiteralString(token.raw, { kind: 'winansi', map: null });
    }
    if (text[i] === '<' && text[i + 1] !== '<') {
      const end = text.indexOf('>', i + 1);
      if (end > i) return decodeHexString(text.slice(i + 1, end), { kind: 'winansi', map: null });
    }
  }
  return '';
}

function extractLinkAnnotations(pageText, objects) {
  const links = [];
  for (const ref of parseAnnotsRefs(pageText, objects)) {
    const annot = objects.get(ref);
    if (!annot || !/\/Subtype\s*\/?Link\b/.test(annot.bodyText)) continue;
    let actionText = annot.bodyText;
    const actionRef = parseIndirectRef(annot.bodyText, 'A');
    if (actionRef && objects.get(actionRef)) actionText += `\n${objects.get(actionRef).bodyText}`;
    const uri = readPdfStringAfterKey(actionText, 'URI').trim();
    if (!uri) continue;
    links.push({
      uri,
      contents: readPdfStringAfterKey(annot.bodyText, 'Contents').trim(),
      objectNumber: ref,
    });
  }
  return links;
}

function parseFontRefs(text) {
  const fontMatch = text.match(/\/Font\s*<<([\s\S]*?)>>/);
  if (!fontMatch) return new Map();
  const out = new Map();
  for (const m of fontMatch[1].matchAll(/\/([^\s/<>{}\[\]()]+)\s+(\d+)\s+\d+\s+R/g)) {
    out.set(m[1], Number(m[2]));
  }
  return out;
}

async function inflate(bytes) {
  const ds = new DecompressionStream('deflate');
  const stream = new Blob([bytes]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function getStreamBytes(obj, objects) {
  if (!obj) return null;
  const bodyText = obj.bodyText;
  const streamIdx = bodyText.indexOf('stream');
  if (streamIdx < 0) return null;

  let dataStart = streamIdx + 6;
  if (bodyText.slice(dataStart, dataStart + 2) === '\r\n') dataStart += 2;
  else if (bodyText[dataStart] === '\r' || bodyText[dataStart] === '\n') dataStart += 1;

  const dict = bodyText.slice(0, streamIdx);
  let length = null;
  const direct = dict.match(/\/Length\s+(\d+)\b/);
  if (direct) length = Number(direct[1]);
  const indirect = dict.match(/\/Length\s+(\d+)\s+\d+\s+R/);
  if (!direct && indirect) {
    const lenObj = objects.get(Number(indirect[1]));
    const parsed = lenObj?.bodyText.match(/\d+/);
    if (parsed) length = Number(parsed[0]);
  }

  let raw;
  if (Number.isFinite(length)) raw = obj.bodyBytes.subarray(dataStart, dataStart + length);
  else {
    const endIdx = bodyText.indexOf('endstream', dataStart);
    raw = obj.bodyBytes.subarray(dataStart, endIdx < 0 ? obj.bodyBytes.length : endIdx);
    while (raw.length && (raw[raw.length - 1] === 10 || raw[raw.length - 1] === 13)) raw = raw.subarray(0, raw.length - 1);
  }

  if (/\/FlateDecode\b/.test(dict)) return inflate(raw);
  return raw;
}

function codePointString(hex) {
  const clean = hex.replace(/\s+/g, '');
  if (!clean) return '';
  try {
    const bytes = new Uint8Array(clean.match(/../g).map(x => parseInt(x, 16)));
    if (bytes.length >= 2 && bytes.length % 2 === 0) {
      let s = '';
      for (let i = 0; i < bytes.length; i += 2) s += String.fromCharCode((bytes[i] << 8) | bytes[i + 1]);
      return s;
    }
    return win1252Decoder.decode(bytes);
  } catch {
    return '';
  }
}

function parseToUnicode(cmapText) {
  const map = new Map();
  for (const block of cmapText.matchAll(/beginbfchar([\s\S]*?)endbfchar/g)) {
    for (const m of block[1].matchAll(/<([0-9A-Fa-f]+)>\s*<([0-9A-Fa-f]+)>/g)) {
      map.set(parseInt(m[1], 16), codePointString(m[2]));
    }
  }
  for (const block of cmapText.matchAll(/beginbfrange([\s\S]*?)endbfrange/g)) {
    const body = block[1];
    for (const m of body.matchAll(/<([0-9A-Fa-f]+)>\s*<([0-9A-Fa-f]+)>\s*\[([^\]]+)\]/g)) {
      const start = parseInt(m[1], 16);
      const end = parseInt(m[2], 16);
      const vals = [...m[3].matchAll(/<([0-9A-Fa-f]+)>/g)].map(x => codePointString(x[1]));
      for (let c = start; c <= end && c - start < vals.length; c++) map.set(c, vals[c - start]);
    }
    for (const m of body.matchAll(/<([0-9A-Fa-f]+)>\s*<([0-9A-Fa-f]+)>\s*<([0-9A-Fa-f]+)>/g)) {
      const start = parseInt(m[1], 16);
      const end = parseInt(m[2], 16);
      const dst = parseInt(m[3], 16);
      for (let c = start; c <= end; c++) map.set(c, String.fromCodePoint(dst + c - start));
    }
  }
  return map;
}

async function buildFontDecoder(fontObjNumber, objects) {
  const obj = objects.get(fontObjNumber);
  if (!obj) return { kind: 'winansi', map: null };
  const toUnicode = parseIndirectRef(obj.bodyText, 'ToUnicode');
  if (toUnicode) {
    const cmapBytes = await getStreamBytes(objects.get(toUnicode), objects);
    const cmap = parseToUnicode(bytesToLatin1(cmapBytes || new Uint8Array()));
    return { kind: 'identity', map: cmap };
  }
  return { kind: /\/Encoding\s*\/WinAnsiEncoding/.test(obj.bodyText) ? 'winansi' : 'latin1', map: null };
}

function decodeLiteralString(raw, decoder) {
  const bytes = [];
  for (let i = 0; i < raw.length; i++) {
    const ch = raw.charCodeAt(i) & 0xff;
    if (ch !== 92) { bytes.push(ch); continue; }
    i++;
    if (i >= raw.length) break;
    const next = raw[i];
    const simple = { n: 10, r: 13, t: 9, b: 8, f: 12, '(': 40, ')': 41, '\\': 92 };
    if (next in simple) bytes.push(simple[next]);
    else if (next === '\r') { if (raw[i + 1] === '\n') i++; }
    else if (next === '\n') { /* line continuation */ }
    else if (/[0-7]/.test(next)) {
      let oct = next;
      for (let j = 0; j < 2 && i + 1 < raw.length && /[0-7]/.test(raw[i + 1]); j++) oct += raw[++i];
      bytes.push(parseInt(oct, 8) & 0xff);
    } else bytes.push(next.charCodeAt(0) & 0xff);
  }
  const arr = Uint8Array.from(bytes);
  if (decoder?.kind === 'identity') {
    let out = '';
    for (let i = 0; i + 1 < arr.length; i += 2) {
      const code = (arr[i] << 8) | arr[i + 1];
      out += decoder.map.get(code) ?? String.fromCodePoint(code);
    }
    return out;
  }
  return (decoder?.kind === 'latin1' ? latin1Decoder : win1252Decoder).decode(arr);
}

function decodeHexString(hex, decoder) {
  const clean = hex.replace(/\s+/g, '');
  if (decoder?.kind === 'identity') {
    let out = '';
    for (let i = 0; i + 3 < clean.length; i += 4) {
      const code = parseInt(clean.slice(i, i + 4), 16);
      out += decoder.map.get(code) ?? String.fromCodePoint(code);
    }
    return out;
  }
  const pairs = clean.match(/../g) || [];
  const bytes = Uint8Array.from(pairs.map(x => parseInt(x, 16)));
  return (decoder?.kind === 'latin1' ? latin1Decoder : win1252Decoder).decode(bytes);
}

function readLiteralToken(text, start) {
  let depth = 1;
  let escaped = false;
  let i = start + 1;
  let raw = '';
  for (; i < text.length; i++) {
    const c = text[i];
    if (escaped) { raw += '\\' + c; escaped = false; continue; }
    if (c === '\\') { escaped = true; continue; }
    if (c === '(') { depth++; raw += c; continue; }
    if (c === ')') { depth--; if (depth === 0) break; raw += c; continue; }
    raw += c;
  }
  return { raw, end: i + 1 };
}

function extractTextFromContent(content, fontDecoders) {
  const lines = [];
  let currentFont = null;
  const tokenRe = /\/([^\s/<>{}\[\]()]+)\s+[-+]?\d*\.?\d+\s+Tf|\[|\(|<(?=[0-9A-Fa-f\s]+>)/g;
  let pos = 0;
  while (pos < content.length) {
    tokenRe.lastIndex = pos;
    const m = tokenRe.exec(content);
    if (!m) break;
    if (m[1]) {
      currentFont = m[1];
      pos = tokenRe.lastIndex;
      continue;
    }
    const decoder = fontDecoders.get(currentFont) || { kind: 'winansi', map: null };
    if (m[0] === '(') {
      const tok = readLiteralToken(content, m.index);
      const after = content.slice(tok.end).match(/^\s*(Tj|'|")/);
      if (after) lines.push(decodeLiteralString(tok.raw, decoder));
      pos = tok.end;
      continue;
    }
    if (m[0] === '<') {
      const end = content.indexOf('>', m.index + 1);
      if (end > 0) {
        const after = content.slice(end + 1).match(/^\s*(Tj|'|")/);
        if (after) lines.push(decodeHexString(content.slice(m.index + 1, end), decoder));
        pos = end + 1;
        continue;
      }
    }
    if (m[0] === '[') {
      let i = m.index + 1;
      let textPart = '';
      for (; i < content.length;) {
        if (content[i] === ']') break;
        if (content[i] === '(') {
          const tok = readLiteralToken(content, i);
          textPart += decodeLiteralString(tok.raw, decoder);
          i = tok.end;
        } else if (content[i] === '<') {
          const end = content.indexOf('>', i + 1);
          if (end < 0) break;
          textPart += decodeHexString(content.slice(i + 1, end), decoder);
          i = end + 1;
        } else i++;
      }
      const after = content.slice(i + 1).match(/^\s*TJ/);
      if (after && textPart) lines.push(textPart);
      pos = i + 1;
      continue;
    }
    pos = tokenRe.lastIndex;
  }
  return lines;
}

export async function extractPdfPages(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);
  if (bytesToLatin1(bytes.subarray(0, 8)).indexOf('%PDF-') !== 0) throw new Error('Wybrany plik nie jest prawidłowym PDF-em.');
  const objects = findObjects(bytes);
  const pages = [...objects.values()]
    .filter(o => /\/Type\s*\/Page\b/.test(o.bodyText))
    .sort((a, b) => a.number - b.number);
  if (!pages.length) throw new Error('Nie znaleziono stron w pliku PDF.');

  const decoderCache = new Map();
  const result = [];
  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    const page = pages[pageIndex];
    const fontRefs = parseFontRefs(page.bodyText);
    const fontDecoders = new Map();
    for (const [name, ref] of fontRefs) {
      if (!decoderCache.has(ref)) decoderCache.set(ref, await buildFontDecoder(ref, objects));
      fontDecoders.set(name, decoderCache.get(ref));
    }
    const parts = [];
    for (const ref of parseContentsRefs(page.bodyText)) {
      const streamBytes = await getStreamBytes(objects.get(ref), objects);
      if (streamBytes) parts.push(bytesToLatin1(streamBytes));
    }
    const lines = parts.flatMap(part => extractTextFromContent(part, fontDecoders))
      .map(s => s.replace(/\u0000/g, '').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    const links = extractLinkAnnotations(page.bodyText, objects);
    result.push({ page: pageIndex + 1, objectNumber: page.number, lines, text: lines.join('\n'), links });
  }
  return result;
}

function normalizeCoord(value) {
  return Number(String(value).replace(',', '.'));
}

function cleanName(value) {
  return String(value || '')
    .replace(/^\s*\d+\s*$/, '')
    .replace(/\s+/g, ' ')
    .replace(/[–—]+/g, '-')
    .trim();
}

function cleanTextLine(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/[–—]+/g, '-')
    .trim();
}

function inferRouteTitle(pages, filename) {
  const coverLines = pages[0]?.lines || [];
  const fromPdf = coverLines.find(x => /\p{L}.+[-–—].+\p{L}/u.test(x));
  const fallback = String(filename || '').replace(/\.pdf$/i, '').replace(/\s*\(\d+\)\s*$/, '');
  return cleanName(fromPdf || fallback || 'Trasa e-TOLL');
}

function inferDirection(routeTitle) {
  const m = routeTitle.match(/[-–—]\s*([^,;]+)$/);
  if (!m) return '';
  return cleanName(m[1]).replace(/^\d+\s*/, '').toLocaleLowerCase('pl-PL');
}

function scoreNameLine(line) {
  if (!line) return -100;
  if (/^\d+$/.test(line)) return -100;
  if (/^(GPS|ADRES|INFORMACJE|DYSPOZYTOR|OPÓŹNIENIA|WAŻNE|INNE|STANOWISKO|STANOWISKA|REGULAMIN|DO\s+.+->)/i.test(line)) return -100;
  let score = 0;
  if (line === line.toLocaleUpperCase('pl-PL')) score += 5;
  if (/\p{L}/u.test(line)) score += 2;
  if (line.length > 3 && line.length < 70) score += 2;
  return score;
}

function isAllCapsText(value) {
  const text = cleanName(value);
  const letters = text.match(/\p{L}/gu) || [];
  return letters.length > 0 && text === text.toLocaleUpperCase('pl-PL');
}

function findTypeStart(lines) {
  return lines.findIndex(line => /^(Dworzec|Przystanek|Przystane|Terminal|Parking)\b/i.test(line));
}

function isSectionLabel(line) {
  return /^(INFORMACJE(?: OGÓLNE)?|ADRES|GPS|DYSPOZYTOR|OPÓŹNIENIA|WAŻNE INFORMACJE|INNE INFORMACJE|STANOWISKA?|REGULAMIN DWORCA)$/i.test(cleanName(line));
}

function joinTitleParts(parts) {
  const clean = parts.map(cleanName).filter(Boolean);
  let out = '';
  for (const part of clean) {
    if (part === '-') {
      out = out.trimEnd() + ' - ';
      continue;
    }
    const noSpaceJoin = out && !out.endsWith(' - ') && /^[\p{Lu}ĄĆĘŁŃÓŚŹŻ]{1,3}$/u.test(out.split(/\s+/).at(-1) || '') && /^[\p{Lu}ĄĆĘŁŃÓŚŹŻ]{2,8}$/u.test(part);
    out += (out && !out.endsWith(' - ') && !noSpaceJoin ? ' ' : '') + part;
  }
  return cleanName(out.replace(/\s*-\s*/g, ' - '));
}

function pageStopName(page) {
  const lines = page.lines.map(cleanName).filter(Boolean);
  const typeStart = findTypeStart(lines);
  if (typeStart > 0) {
    const parts = [];
    for (let i = typeStart - 1; i >= 0; i--) {
      const line = lines[i];
      if (isSectionLabel(line)) {
        if (parts.length) break;
        continue;
      }
      if (line === '-' || isAllCapsText(line)) parts.unshift(line);
      else break;
    }
    const title = joinTitleParts(parts);
    if (title && scoreNameLine(title) > -50) return title;
  }

  const gpsIndex = lines.findIndex(line => /^GPS\b/i.test(line));
  const beforeGps = lines.slice(0, gpsIndex > 0 ? gpsIndex : Math.min(lines.length, 16));
  const candidates = beforeGps
    .map((line, index) => ({ line, index }))
    .filter(x => scoreNameLine(x.line) > -50)
    .sort((a, b) => scoreNameLine(b.line) - scoreNameLine(a.line) || b.index - a.index);
  return candidates[0]?.line || `Punkt ze strony ${page.page}`;
}

function normalizeAddressParts(parts) {
  let text = parts.map(cleanTextLine).filter(Boolean).join(' ')
    .replace(/\s+([,.;])/g, '$1')
    .replace(/\bu\s+l\s*\.\s*/gi, 'ul. ')
    .replace(/\b(ul|al|pl)\s*\.\s*/gi, (_, p) => `${p}. `)
    .replace(/\s+/g, ' ')
    .trim();

  text = text
    .replace(/\b(\d{2})\s*-\s*(\d)\s*(\d)\s*(\d)\b/g, '$1-$2$3$4')
    .replace(/\b(\d{2})\s*-\s*(\d{2})\s*(\d)\b/g, '$1-$2$3')
    .replace(/\b(\d{2})\s*-\s*(\d{3})\b/g, '$1-$2')
    .replace(/\b(\d{3})\s*-\s*(\d{2})\b/g, '$1-$2')
    .replace(/\s+(\d{2}-\d{3}\b)/, ', $1')
    .replace(/,\s*-\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .trim();
  return text;
}

function pageAddress(page) {
  const lines = page.lines.map(cleanTextLine).filter(Boolean);
  const addressStart = lines.findIndex(line => /^ADRES\b/i.test(line));
  if (addressStart < 0) return '';
  const typeStart = findTypeStart(lines);
  let titleStart = typeStart;
  if (typeStart > addressStart) {
    for (let i = typeStart - 1; i > addressStart; i--) {
      const line = lines[i];
      if (line === '-' || isAllCapsText(line)) titleStart = i;
      else break;
    }
  }
  const gpsIndex = lines.findIndex((line, i) => i > addressStart && /^GPS\b/i.test(line));
  const end = titleStart > addressStart ? titleStart : (gpsIndex > addressStart ? gpsIndex : Math.min(lines.length, addressStart + 14));
  return normalizeAddressParts(lines.slice(addressStart + 1, end));
}

function coordinatesFromPageText(page) {
  const coordRe = /(-?\d{1,2}(?:[.,]\d{4,}))\s*[,;]\s*(-?\d{1,3}(?:[.,]\d{4,}))/g;
  const found = [];
  const sources = [page.text];
  const gpsPos = page.text.search(/(?:^|\n)GPS\b/i);
  if (gpsPos >= 0) {
    const compact = page.text.slice(gpsPos, gpsPos + 260).replace(/\s+/g, '');
    sources.push(compact);
  }
  const seen = new Set();
  for (const source of sources) {
    coordRe.lastIndex = 0;
    let m;
    while ((m = coordRe.exec(source))) {
      const lat = normalizeCoord(m[1]);
      const lon = normalizeCoord(m[2]);
      if (!validCoords(lat, lon)) continue;
      const key = coordinateQuery(lat, lon);
      if (seen.has(key)) continue;
      seen.add(key);
      found.push({ lat, lon, index: m.index, source });
    }
  }
  return found;
}

function safeDecodeUrl(url) {
  let value = String(url || '').replace(/&amp;/g, '&');
  for (let i = 0; i < 3; i++) {
    try {
      const decoded = decodeURIComponent(value);
      if (decoded === value) break;
      value = decoded;
    } catch {
      break;
    }
  }
  return value;
}

function validCoords(latValue, lonValue) {
  if (
    latValue === null || latValue === undefined || latValue === '' ||
    lonValue === null || lonValue === undefined || lonValue === ''
  ) return false;

  const lat = normalizeCoord(latValue);
  const lon = normalizeCoord(lonValue);
  return Number.isFinite(lat) && Number.isFinite(lon) &&
    Math.abs(lat) <= 90 && Math.abs(lon) <= 180 &&
    !(lat === 0 && lon === 0);
}

function dmsToDecimal(degrees, minutes, seconds, hemisphere) {
  let value = Number(degrees) + Number(minutes) / 60 + Number(seconds) / 3600;
  if (/^[SW]$/i.test(hemisphere)) value *= -1;
  return value;
}

function coordsFromDmsText(value) {
  const text = safeDecodeUrl(value).replace(/\+/g, ' ');
  const re = /(\d{1,3})\s*(?:°|º|deg)\s*(\d{1,2})\s*['′]\s*(\d{1,2}(?:\.\d+)?)\s*["″]?\s*([NS])[^\d]+(\d{1,3})\s*(?:°|º|deg)\s*(\d{1,2})\s*['′]\s*(\d{1,2}(?:\.\d+)?)\s*["″]?\s*([EW])/i;
  const m = text.match(re);
  if (!m) return null;
  const lat = dmsToDecimal(m[1], m[2], m[3], m[4]);
  const lon = dmsToDecimal(m[5], m[6], m[7], m[8]);
  return validCoords(lat, lon) ? { lat, lon, pattern: 'google-dms' } : null;
}

function coordsFromGoogleMapsUrl(url) {
  const decoded = safeDecodeUrl(url).replace(/\+/g, ' ');

  // Najpierw współrzędne celu. W pełnym linku Google Maps zwykle są zapisane
  // jako !3d...!4d.... Są dokładniejsze niż współrzędne środka mapy po znaku @.
  const targetPatterns = [
    { pattern: 'google-place', regex: /!3d(-?\d{1,2}(?:\.\d+)?)!4d(-?\d{1,3}(?:\.\d+)?)/g },
    { pattern: 'google-query', regex: /[?&](?:q|query|ll|destination|daddr|saddr|center)=\s*(?:loc:)?\s*(-?\d{1,2}(?:\.\d+)?)\s*(?:%2C|,|%20|\s)\s*(-?\d{1,3}(?:\.\d+)?)/gi },
    { pattern: 'google-path', regex: /\/(?:place|search|dir)\/[^?#]*?(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)(?:[/?!]|$)/gi },
  ];
  for (const group of targetPatterns) {
    const matches = [...decoded.matchAll(group.regex)];
    for (let i = matches.length - 1; i >= 0; i--) {
      const lat = Number(matches[i][1]);
      const lon = Number(matches[i][2]);
      if (validCoords(lat, lon)) return { lat, lon, pattern: group.pattern };
    }
  }

  const dms = coordsFromDmsText(decoded);
  if (dms) return dms;

  // Ostatecznie używamy środka mapy. Ten zapis występuje po @lat,lon,zoom.
  const mapCenter = [...decoded.matchAll(/@(-?\d{1,2}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)(?:,|\/|$)/g)];
  for (let i = mapCenter.length - 1; i >= 0; i--) {
    const lat = Number(mapCenter[i][1]);
    const lon = Number(mapCenter[i][2]);
    if (validCoords(lat, lon)) return { lat, lon, pattern: 'google-at' };
  }
  return null;
}

function coordsFromLinkContents(value) {
  const text = cleanTextLine(value);
  if (!text) return null;

  const match = text.match(/(?:\bGPS\b\s*)?(-?\d{1,2}(?:[.,]\d{4,}))\s*[,;]\s*(-?\d{1,3}(?:[.,]\d{4,}))/i);
  if (!match) return null;

  const lat = normalizeCoord(match[1]);
  const lon = normalizeCoord(match[2]);
  return validCoords(lat, lon) ? { lat, lon, pattern: 'google-annotation-contents' } : null;
}

function isShortGoogleMapsUrl(url) {
  return /^(?:https?:\/\/)?(?:maps\.app\.goo\.gl|goo\.gl\/maps)\//i.test(String(url || '').trim());
}

function normalizeKey(value) {
  return cleanName(value).toLocaleLowerCase('pl-PL').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9.-]+/g, ' ').trim();
}

function coordinateQuery(lat, lon) {
  return `${Number(lat).toFixed(7)}, ${Number(lon).toFixed(7)}`;
}

function addUniqueQuery(list, value) {
  const query = cleanTextLine(value)
    .replace(/\s+,/g, ',')
    .replace(/,+/g, ',')
    .replace(/,\s*,/g, ', ')
    .trim();
  if (!query) return;
  const key = normalizeKey(query);
  if (!key || list.some(item => normalizeKey(item) === key)) return;
  list.push(query);
}

function addressCity(address) {
  const text = cleanTextLine(address);
  const postal = text.match(/\b\d{2}-\d{3}\s+(.+)$/);
  if (postal) return cleanTextLine(postal[1]);
  const commaParts = text.split(',').map(cleanTextLine).filter(Boolean);
  return commaParts.length > 1 ? commaParts.at(-1).replace(/^\d{2}-\d{3}\s+/, '').trim() : '';
}

function addressWithoutPostal(address) {
  return cleanTextLine(address)
    .replace(/,?\s*\b\d{2}-\d{3}\b\s*/g, ', ')
    .replace(/^\s*(ul|al|pl)\.\s*/i, '')
    .replace(/,\s*,/g, ', ')
    .replace(/^,|,$/g, '')
    .trim();
}

function buildSearchQueries(name, address, primary = '') {
  const queries = [];
  addUniqueQuery(queries, primary);
  if (/^-?\d{1,2}(?:\.\d+)?\s*,\s*-?\d{1,3}(?:\.\d+)?$/.test(primary)) return queries;

  const city = addressCity(address);
  const compactAddress = addressWithoutPostal(address);
  const cleanStopName = cleanName(name)
    .replace(/\s+-\s+(?:ODJAZDY|PRZYJAZDY)$/i, '')
    .trim();

  addUniqueQuery(queries, address);
  addUniqueQuery(queries, compactAddress);
  if (city && compactAddress && !normalizeKey(compactAddress).includes(normalizeKey(city))) {
    addUniqueQuery(queries, `${compactAddress}, ${city}`);
  }
  if (city && compactAddress) addUniqueQuery(queries, `${city}, ${compactAddress}`);
  if (cleanStopName && address) addUniqueQuery(queries, `${cleanStopName}, ${address}`);
  if (cleanStopName && city) addUniqueQuery(queries, `${cleanStopName}, ${city}`);
  if (cleanStopName) addUniqueQuery(queries, cleanStopName);

  const nameIsCity = city && normalizeKey(cleanStopName) === normalizeKey(city);
  if (city && (/dworzec/i.test(cleanStopName) || nameIsCity)) {
    addUniqueQuery(queries, `Dworzec Autobusowy ${city}`);
    addUniqueQuery(queries, `${city} Dworzec Autobusowy`);
    addUniqueQuery(queries, `Dworzec ${city}`);
  }
  return queries.slice(0, 8);
}

export async function extractRouteFromPdf(arrayBuffer, filename = '') {
  const pages = await extractPdfPages(arrayBuffer);
  const routeTitle = inferRouteTitle(pages, filename);
  const direction = inferDirection(routeTitle);
  const all = [];

  for (const page of pages) {
    const name = pageStopName(page);
    const address = pageAddress(page);
    const pageCandidates = [];
    const googleLinks = [];
    const seenGoogleUrls = new Set();
    for (const link of (page.links || [])) {
      if (!/(?:google\.[^/]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(link.uri)) continue;
      const key = safeDecodeUrl(link.uri).trim();
      if (!key || seenGoogleUrls.has(key)) continue;
      seenGoogleUrls.add(key);
      googleLinks.push(link);
    }

    // Najwyższy priorytet ma aktywny napis „GPS” w PDF-ie. W tym pliku
    // współrzędne celu są zapisane w /Contents adnotacji linku, np.
    // „GPS 50.259298, 19.010331”. Dzięki temu nie trzeba geokodować adresu
    // ani używać współrzędnych środka widoku Google Maps.
    const seenAnnotationCoords = new Set();
    for (const link of googleLinks) {
      const found = coordsFromLinkContents(link.contents);
      if (!found) continue;
      const key = coordinateQuery(found.lat, found.lon);
      if (seenAnnotationCoords.has(key)) continue;
      seenAnnotationCoords.add(key);
      pageCandidates.push({
        id: crypto.randomUUID(), page: page.page, name, lat: found.lat, lon: found.lon,
        query: key, searchQueries: [key], address, direction: '',
        source: 'Współrzędne odczytane z aktywnego napisu GPS w PDF',
        sourceType: 'google-maps-annotation-gps', mapsUrl: link.uri,
        mapsPattern: found.pattern,
      });
    }

    // Jeżeli adnotacja linku nie zawiera GPS, odczytujemy współrzędne z tekstu
    // strony PDF. Ten krok nadal ma pierwszeństwo przed adresem.
    if (!pageCandidates.length) {
      for (const coordinate of coordinatesFromPageText(page)) {
        const { lat, lon } = coordinate;
        const nearby = coordinate.source.slice(coordinate.index, Math.min(coordinate.source.length, coordinate.index + 180));
        const dm = nearby.match(/kierunek\s+([^\n,;]+)/i);
        const query = coordinateQuery(lat, lon);
        pageCandidates.push({
          id: crypto.randomUUID(), page: page.page, name, lat, lon,
          query, searchQueries: [query], address,
          direction: cleanName(dm?.[1] || ''), source: 'Współrzędne zapisane w PDF',
          sourceType: 'gps-text', mapsUrl: googleLinks[0]?.uri || '',
        });
      }
    }

    // Pełny link Google Maps może zawierać docelowy punkt bezpośrednio w URL.
    // Nie traktujemy zapisu @lat,lon jako pewnego celu, ponieważ często jest to
    // jedynie środek aktualnego widoku mapy.
    if (!pageCandidates.length) {
      const seenLinkCoords = new Set();
      for (const link of googleLinks) {
        const found = coordsFromGoogleMapsUrl(link.uri);
        if (!found || found.pattern === 'google-at') continue;
        const key = coordinateQuery(found.lat, found.lon);
        if (seenLinkCoords.has(key)) continue;
        seenLinkCoords.add(key);
        pageCandidates.push({
          id: crypto.randomUUID(), page: page.page, name, lat: found.lat, lon: found.lon,
          query: key, searchQueries: [key], address, direction: '',
          source: 'Współrzędne odczytane z aktywnego linku Google Maps',
          sourceType: 'google-maps-link', mapsUrl: link.uri,
          mapsPattern: found.pattern,
        });
      }
    }

    const hasGpsMarker = page.lines.some(line => /^GPS\b/i.test(cleanName(line)));
    if (!pageCandidates.length && (hasGpsMarker || googleLinks.length)) {
      const linksToResolve = googleLinks.length ? googleLinks : [{ uri: '', contents: '' }];
      for (let linkIndex = 0; linkIndex < linksToResolve.length; linkIndex++) {
        const link = linksToResolve[linkIndex];
        const shortLink = isShortGoogleMapsUrl(link.uri);
        const searchQueries = buildSearchQueries(name, address, address);
        pageCandidates.push({
          id: crypto.randomUUID(), page: page.page,
          name: linksToResolve.length > 1 ? `${name} (${linkIndex + 1})` : name,
          lat: null, lon: null,
          query: searchQueries[0] || address || link.uri,
          searchQueries, address, direction: '',
          source: link.uri
            ? 'Aktywny link Google Maps wymaga rozwiązania; adres nie będzie użyty zamiast linku'
            : 'Brak jawnych współrzędnych i aktywnego linku Google Maps',
          sourceType: shortLink ? 'google-maps-short-address' : (link.uri ? 'google-maps-unresolved' : 'address'),
          mapsUrl: link.uri || '',
        });
      }
    }

    all.push(...pageCandidates);
  }

  const selected = [];
  const byPage = new Map();
  for (const point of all) {
    if (!byPage.has(point.page)) byPage.set(point.page, []);
    byPage.get(point.page).push(point);
  }
  for (const pagePoints of byPage.values()) {
    const unique = [];
    const seen = new Set();
    for (const point of pagePoints) {
      const key = point.mapsUrl
        ? `url:${safeDecodeUrl(point.mapsUrl)}`
        : validCoords(point.lat, point.lon)
          ? `gps:${coordinateQuery(point.lat, point.lon)}`
          : `query:${normalizeKey(point.query)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(point);
    }
    selected.push(...unique);
  }

  const deduped = [];
  for (const point of selected.sort((a, b) => a.page - b.page)) {
    const prev = deduped[deduped.length - 1];
    const sameCoords = prev && validCoords(prev.lat, prev.lon) && validCoords(point.lat, point.lon)
      && Math.abs(normalizeCoord(prev.lat) - normalizeCoord(point.lat)) < 1e-7
      && Math.abs(normalizeCoord(prev.lon) - normalizeCoord(point.lon)) < 1e-7;
    const sameMapLink = prev && point.mapsUrl && prev.mapsUrl
      && safeDecodeUrl(prev.mapsUrl) === safeDecodeUrl(point.mapsUrl);
    const sameQuery = prev && !point.mapsUrl && !prev.mapsUrl
      && normalizeKey(prev.query) === normalizeKey(point.query);
    if (sameCoords || sameMapLink || sameQuery) continue;
    deduped.push(point);
  }

  if (deduped.length < 2) {
    throw new Error(`Znaleziono tylko ${deduped.length} punkt trasy. Wtyczka szuka współrzędnych, linków Google Maps albo adresu pod nagłówkiem ADRES.`);
  }

  const stats = {
    gpsText: deduped.filter(p => p.sourceType === 'gps-text').length,
    googleMaps: deduped.filter(p => p.sourceType === 'google-maps-link').length,
    googleMapsAnnotationGps: deduped.filter(p => p.sourceType === 'google-maps-annotation-gps').length,
    shortGoogleMaps: deduped.filter(p => p.sourceType === 'google-maps-short-address').length,
    address: deduped.filter(p => p.sourceType === 'address').length,
  };
  return { routeTitle, direction, pagesCount: pages.length, allPoints: all, points: deduped, stats };
}
